import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CropShowcase = () => {  
  const crops = [
    { image: '/Almonds.jpg', alt: 'Almonds', label: 'Almonds pH 6.5-7.0', link: '/almond' },
    { image: '/Apples.jpg', alt: 'Apples', label: 'Apples pH 6.0-6.5', link: '/apple' },
    { image: '/Blueberries.jpg', alt: 'Blueberries', label: 'Blueberries pH 4.5-5.5', link: '/blueberry' },
    { image: '/Cannibis.jpg', alt: 'Cannabis', label: 'Cannabis pH 5.8-6.5', link: '/cannabis' },
    { image: '/Cherry-M.jpg', alt: 'Cherries', label: 'Cherries pH 6.0-7.0', link: '/cherry' },
    { image: '/Grapes.jpg', alt: 'Grapes', label: 'Grapes pH 5.5-6.5', link: '/grape' },
    { image: '/Hops2.jpg', alt: 'Hops', label: 'Hops pH 6.0-6.5', link: '/hops' },
    { image: '/Raspberries.jpg', alt: 'Raspberries', label: 'Raspberries pH 5.5-6.5', link: '/raspberry' },
    { image: '/Tomatos.jpg', alt: 'Tomatoes', label: 'Tomatoes pH 6.0-6.8', link: '/tomato' }
  ];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, imagePath: string) => {
    console.error(`Failed to load image: ${imagePath}`);
    console.log(`Attempted URL: ${window.location.origin}${imagePath}`);
    
    // Try alternative approaches
    const img = e.currentTarget;
    
    // First fallback: try without leading slash
    if (imagePath.startsWith('/')) {
      const altPath = imagePath.substring(1);
      console.log(`Trying alternative path: ${altPath}`);
      img.src = altPath;
      return;
    }
    
    // Final fallback: hide the image
    img.style.display = 'none';
    
    // Add a placeholder background
    const container = img.parentElement;
    if (container) {
      container.style.background = 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)';
      container.innerHTML = `
        <div class="flex items-center justify-center h-full">
          <div class="text-center text-gray-500">
            <div class="text-4xl mb-2">🌱</div>
            <div class="text-sm font-medium">${imagePath.split('/').pop()?.split('.')[0]}</div>
          </div>
        </div>
      `;
    }
  };

  const CropItem = ({ crop, index }: { crop: any, index: number }) => {
    const ItemContent = () => (
      <div className="aspect-[4/3] w-full relative overflow-hidden">
        <img
          src={crop.image}
          alt={crop.alt}
          className="w-full h-full object-cover transition-all duration-500 group-hover:brightness-110 group-hover:contrast-110"
          onError={(e) => handleImageError(e, crop.image)}
          onLoad={() => console.log(`Successfully loaded: ${crop.image}`)}
        />
        {/* Darker overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Crop label - just text */}
        <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <span className="text-white font-bold text-lg drop-shadow-lg">{crop.label}</span>
        </div>
      </div>
    );

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ 
          duration: 0.6, 
          delay: index * 0.1,
          ease: "easeOut"
        }}
        whileHover={{ 
          scale: 1.05,
          zIndex: 10,
          transition: { duration: 0.3 }
        }}
        className="flex-1 relative group cursor-pointer"
        style={{ minWidth: `${100/9}%` }}
      >
        {crop.link ? (
          <Link to={crop.link}>
            <ItemContent />
          </Link>
        ) : (
          <ItemContent />
        )}
      </motion.div>
    );
  };

  return (
    <section className="w-full overflow-hidden -mt-4 md:mt-0">
      {/* Desktop: Single row */}
      <div className="hidden lg:flex w-full">
        {crops.map((crop, index) => (
          <CropItem key={index} crop={crop} index={index} />
        ))}
      </div>

      {/* Tablet: 3 rows of 3 */}
      <div className="hidden md:grid lg:hidden grid-cols-3 w-full gap-0.5">
        {crops.map((crop, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: index * 0.05,
              ease: "easeOut"
            }}
            whileHover={{ 
              scale: 1.05,
              zIndex: 10,
              transition: { duration: 0.3 }
            }}
            className="relative group cursor-pointer"
          >
            {crop.link ? (
              <Link to={crop.link}>
                <div className="aspect-square w-full relative overflow-hidden">
                  <img
                    src={crop.image}
                    alt={crop.alt}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:brightness-110 group-hover:contrast-110"
                    onError={(e) => handleImageError(e, crop.image)}
                    onLoad={() => console.log(`Successfully loaded: ${crop.image}`)}
                  />
                  {/* Darker overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Crop label - just text */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <span className="text-white font-bold text-base drop-shadow-lg">{crop.label}</span>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="aspect-square w-full relative overflow-hidden">
                <img
                  src={crop.image}
                  alt={crop.alt}
                  className="w-full h-full object-cover transition-all duration-500 group-hover:brightness-110 group-hover:contrast-110"
                  onError={(e) => handleImageError(e, crop.image)}
                  onLoad={() => console.log(`Successfully loaded: ${crop.image}`)}
                />
                {/* Darker overlay for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Crop label - just text */}
                <div className="absolute bottom-0 left-0 right-0 p-3 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-white font-bold text-base drop-shadow-lg">{crop.label}</span>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Mobile: 3 rows of 3 */}
      <div className="grid md:hidden grid-cols-3 w-full gap-0.5">
        {crops.map((crop, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: index * 0.05,
              ease: "easeOut"
            }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.3 }
            }}
            className="relative group cursor-pointer"
          >
            {crop.link ? (
              <Link to={crop.link}>
                <div className="aspect-square w-full relative overflow-hidden">
                  <img
                    src={crop.image}
                    alt={crop.alt}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:brightness-110 aspect-square"
                    onError={(e) => handleImageError(e, crop.image)}
                    onLoad={() => console.log(`Successfully loaded: ${crop.image}`)}
                  />
                  {/* Darker overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Crop label - just text */}
                  <div className="absolute bottom-0 left-0 right-0 p-1 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <span className="text-white font-bold text-xs drop-shadow-lg">{crop.label}</span>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="aspect-square w-full relative overflow-hidden">
                <img
                  src={crop.image}
                  alt={crop.alt}
                  className="w-full h-full object-cover transition-all duration-500 group-hover:brightness-110"
                  onError={(e) => handleImageError(e, crop.image)}
                  onLoad={() => console.log(`Successfully loaded: ${crop.image}`)}
                />
                {/* Darker overlay for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Crop label - just text */}
                <div className="absolute bottom-0 left-0 right-0 p-2 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-white font-bold text-sm drop-shadow-lg">{crop.label}</span>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default CropShowcase;